<template>
	<div class="card !shadow-none !border-0">
		<div v-if="isLoading" class="flex items-center justify-center py-10">
			<i class="tabler:loader-2 w-8 h-8 animate-spin text-primary"></i>
			<span class="ml-2">Loading assessment...</span>
		</div>

		<div v-else-if="fetchError" class="p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
			<div class="flex items-center">
				<i class="tabler:alert-circle text-red-600 text-lg mr-2"></i>
				<span class="text-red-800">{{ fetchError }}</span>
			</div>
		</div>

		<div v-else-if="totalQuestions === 0" class="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
			<div class="flex items-center justify-between gap-3">
				<div class="flex items-center">
					<i class="tabler:alert-triangle text-yellow-600 text-lg mr-2"></i>
					<span class="text-yellow-900">No assessment questions available.</span>
				</div>
				<EwButton label="Close" type="secondary" @click="handleClose" />
			</div>
		</div>

		<AssessmentResult
			v-else-if="isCompleted"
			:title="assessmentTitle"
			:totalQuestions="totalQuestions"
			:answeredQuestions="answeredCount"
			:correctAnswers="correctAnswers"
			:totalPoints="totalPoints"
			:earnedPoints="earnedPoints"
			:percentage="scorePercentage"
			:elapsedSeconds="elapsedSeconds"
			:passingPercent="passingPercent"
			:results="evaluationResults"
			:competencyScores="competencyScores"
			:questionTypeScores="questionTypeScores"
			:criticalLevelScores="criticalLevelScores"
			:attemptHistory="attemptHistory"
			:retakeCount="retakeCount"
			@retake="handleRetake"
			@close="handleClose"
		/>

		<template v-else-if="!hasStarted">
			<div class="rounded-lg border border-gray-200 overflow-hidden">
				<header class="p-4 bg-gray-50 border-b border-gray-200">
					<h3 class="text-lg font-semibold text-gray-900">{{ assessmentTitle }}</h3>
					<p v-if="assessmentLeadText" class="text-sm text-gray-600 mt-1">{{ assessmentLeadText }}</p>
				</header>

				<section class="p-4 space-y-4">
					<div class="grid grid-cols-1 md:grid-cols-3 gap-3">
						<div class="rounded-md border border-gray-200 p-3">
							<div class="text-xs text-gray-500 uppercase">Questions</div>
							<div class="text-base font-semibold text-gray-900 mt-1">{{ totalQuestions }}</div>
						</div>
						<div class="rounded-md border border-gray-200 p-3">
							<div class="text-xs text-gray-500 uppercase">Passing Score</div>
							<div class="text-base font-semibold text-gray-900 mt-1">{{ passingPercent }}%</div>
						</div>
						<div class="rounded-md border border-gray-200 p-3">
							<div class="text-xs text-gray-500 uppercase">Time Limit</div>
							<div class="text-base font-semibold text-gray-900 mt-1">{{ formattedTimeLimit }}</div>
						</div>
					</div>

					<div class="rounded-md border border-blue-100 bg-blue-50 p-3">
						<div class="text-sm text-blue-900 leading-relaxed">
							{{ userConsentText }}
						</div>
					</div>

					<label class="flex items-start gap-3 p-3 rounded-md border border-gray-200 cursor-pointer select-none">
						<input
							v-model="hasMonitoringConsent"
							type="checkbox"
							class="sr-only"
							aria-label="Monitoring consent"
						/>
						<span
							class="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors"
							:class="hasMonitoringConsent ? 'border-primary bg-primary text-white' : 'border-gray-400 bg-white text-transparent'"
						>
							<span class="text-xs font-bold leading-none">✓</span>
						</span>
						<span class="text-sm text-gray-800">
							I understand and consent to monitoring of this assessment session.
						</span>
					</label>
					<div class="text-xs" :class="hasMonitoringConsent ? 'text-green-700' : 'text-gray-600'">
						{{ hasMonitoringConsent ? 'Consent confirmed.' : 'Please check the box to enable Start Assessment.' }}
					</div>

					<div v-if="startValidationMessage" class="text-sm text-danger">
						{{ startValidationMessage }}
					</div>
				</section>

				<footer class="p-4 border-t border-gray-200 flex justify-end gap-2">
					<EwButton label="Close" type="secondary" @click="handleClose" />
					<EwButton
						label="Start Assessment"
						type="success"
						iconAppend="tabler:player-play"
						:disabled="!hasMonitoringConsent"
						@click="startAssessment"
					/>
				</footer>
			</div>
		</template>

		<template v-else>
			<div class="rounded-lg border border-gray-200 overflow-hidden">
				<header class="p-4 bg-gray-50 border-b border-gray-200">
					<div class="text-xs font-semibold uppercase tracking-wide text-gray-500">
						Assessment Progress
					</div>
					<div class="mt-2 flex items-start">
						<div class="w-[90%] flex  gap-3 flex-col">
							<div class="mt-1 text-sm font-semibold text-gray-800">
								{{ labels.questionLabel || 'Question' }} {{ currentQuestionNumber }} / {{ totalQuestions }}
							</div>
							<div class="w-full h-2 rounded-full bg-gray-200 overflow-hidden">
							<div class="h-2 bg-primary transition-all duration-300" :style="{ width: progressWidth }"></div>
							</div>
					<div class="mt-2 text-xs text-gray-600">Answered: {{ answeredCount }} / {{ totalQuestions }}</div>

						</div>
						<div class="w-[10%] text-right flex flex-col gap-3">
							<div class="text-[11px] uppercase tracking-wide text-gray-500">Elapsed</div>
							<div class="text-xl font-semibold leading-none text-gray-900 tabular-nums">{{ formattedElapsed }}</div>
						</div>
					</div>
				</header>

				<section class="p-4">
					<InplaceEdit
						:inputProp="{ propName: 'content', inputType: 'quiz', content: currentQuestion }"
						method="PUT"
						:href="currentQuestion?._links?.self?.href"
						blockTitle="Edit Question"
						:showAssistant="true"
					>
						<h4 class="text-base font-semibold text-gray-900 leading-relaxed">
							{{ currentQuestionNumber }}. {{ currentQuestion?.question || '' }}
						</h4>
					</InplaceEdit>

					<div class="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
						<InteractiveImage alt="Question illustration" preview class="rounded">
							<template #indicatoricon>
								<i class="tabler:eye w-8 h-8 text-white"></i>
							</template>
							<template #image>
								<div class="w-full h-[180px] md:h-[220px] rounded overflow-hidden bg-gray-100">
									<img
										:src="displayedQuestionImageSrc"
										:alt="displayedQuestionImageTitle"
										class="w-full h-full object-contain"
										@error="handleQuestionImageError"
									/>
								</div>
							</template>
							<template #preview="slotProps">
								<div class="p-5 w-[70%] mx-auto">
									<div class="text-mb mb-3 font-bold text-white">
										{{ displayedQuestionImageTitle }}
									</div>
									<img
										:src="displayedQuestionImageSrc"
										:alt="displayedQuestionImageTitle"
										class="mx-auto max-h-[720px]"
										:style="slotProps.style"
										@error="handleQuestionImageError"
										@click="slotProps.onClick"
									/>
								</div>
							</template>
						</InteractiveImage>
						<div class="mt-2 text-xs text-gray-600 italic text-center fw-semibold">
							{{ displayedQuestionImageTitle }}
						</div>
					</div>

					<p class="text-xs text-gray-600 mt-2">
						{{ currentQuestionIsMulti ? 'Select all correct answers.' : 'Select one correct answer.' }}
					</p>

					<div class="mt-4 space-y-2">
						<label
							v-for="(answer, answerIndex) in currentAnswers"
							:key="answer.id ?? `${currentQuestionNumber}-${answerIndex}`"
							class="flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors"
							:class="isSelected(currentQuestionIndex, answerIndex) ? 'border-primary bg-primary-50' : 'border-gray-200 hover:border-gray-300'"
						>
							<input
								class="mt-1"
								:type="currentQuestionIsMulti ? 'checkbox' : 'radio'"
								:name="`assessment-question-${currentQuestionIndex}`"
								:checked="isSelected(currentQuestionIndex, answerIndex)"
								@change="toggleAnswer(currentQuestionIndex, answerIndex)"
							/>
							<span class="text-sm text-gray-800">
								<strong class="mr-1">{{ answer.answerLabel || optionLabel(answerIndex) }}</strong>
								{{ answer.answer }}
							</span>
						</label>
					</div>

					<div v-if="validationMessage" class="mt-3 text-sm text-danger">
						{{ validationMessage }}
					</div>
				</section>

				<footer class="p-4 border-t border-gray-200 flex items-center gap-2">
					<div class="text-sm font-semibold text-gray-700">
						Points for this question: {{ currentQuestionPoints }}
					</div>
					<div class="ml-auto flex items-center gap-2">
						<EwButton
							label="Back"
							type="secondary"
							icon="tabler:arrow-narrow-left"
							:disabled="currentQuestionIndex === 0"
							@click="goToPreviousQuestion"
						/>
						<EwButton
							v-if="!isLastQuestion"
							label="Next"
							type="success"
							iconAppend="tabler:arrow-narrow-right"
							@click="goToNextQuestion"
						/>
						<EwButton
							v-else
							label="Submit Assessment"
							type="danger"
							iconAppend="tabler:send"
							@click="submitAssessment"
						/>
					</div>
				</footer>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { messages } from '@/assets/language';
import type { LanguageCodes, QuizQuestionsItem } from '@/types';
import { useAppStore } from '@/stores/app-store';
import InplaceEdit from './InplaceEdit/InplaceEdit.vue';
import AssessmentResult from './AssessmentResult.vue';

type QuizAnswer = {
	id?: string;
	answer?: string;
	answerLabel?: string;
	explanation?: string;
	isCorrect?: boolean;
};

type IllustrationItem = {
	id?: string | number;
	identifier?: string | number;
	imageSrc?: string;
	imageSrcEdit?: string;
	imageTitle?: string;
	imageDescription?: string;
	imageCopyright?: string;
};

type AssessmentSource = {
	assessmentQuiz?: {
		questions?: QuizQuestionsItem[];
		passingPercent?: number;
		userConsentText?: string;
		consentText?: string;
		timeLimit?: string | number | null;
		timeLimitUnit?: string;
		timeLimitSeconds?: number | string;
		timeLimitMinutes?: number | string;
		duration?: string | number | null;
		durationUnit?: string;
		durationSeconds?: number | string;
		durationMinutes?: number | string;
		scoringWeights?: {
			competency?: Record<string, number | string>;
			questionType?: Record<string, number | string>;
			typeOfQuestion?: Record<string, number | string>;
			criticalLevel?: Record<string, number | string>;
		};
		criteriaWeights?: {
			competency?: Record<string, number | string>;
			questionType?: Record<string, number | string>;
			typeOfQuestion?: Record<string, number | string>;
			criticalLevel?: Record<string, number | string>;
		};
	};
	mainOutcome?: {
		title?: string;
		leadText?: string;
	};
	illustration?: IllustrationItem[];
	_links?: {
		assessmentQuiz?: { href: string };
		assessmentQuizEdit?: { href: string };
	};
};

type EvaluationResult = {
	questionNumber: number;
	questionText: string;
	isCorrect: boolean;
	basePoints: number;
	pointsEarned: number;
	pointsPossible: number;
	competency: string;
	questionType: string;
	criticalLevel: string;
	competencyWeight: number;
	questionTypeWeight: number;
	criticalLevelWeight: number;
	totalWeightMultiplier: number;
	selectedAnswerLabels: string[];
	correctAnswerLabels: string[];
	explanation: string;
};

type CriteriaScoreItem = {
	label: string;
	earnedPoints: number;
	totalPoints: number;
	questionCount: number;
};

type AttemptHistoryItem = {
	attempt: number;
	percentage: number;
	earnedPoints: number;
	totalPoints: number;
	correctAnswers: number;
	totalQuestions: number;
	elapsedSeconds: number;
	createdAt: string;
};

const props = defineProps<{
	selectedLanguage?: LanguageCodes;
	overrideURL?: string;
	jsonData: AssessmentSource;
}>();

const emits = defineEmits<{
	close: [];
	submit: [Record<string, unknown>];
}>();

const { currentLng } = useAppStore();
const labels = computed(() => {
	const key = currentLng.value as keyof typeof messages;
	return messages[key] ?? {};
});

const isLoading = ref(false);
const fetchError = ref<string | null>(null);
const fetchedData = ref<AssessmentSource | null>(null);

const questions = computed<QuizQuestionsItem[]>(() => {
	const fetchedQuestions = fetchedData.value?.assessmentQuiz?.questions;
	if (Array.isArray(fetchedQuestions)) {
		return fetchedQuestions;
	}
	const inlineQuestions = props.jsonData?.assessmentQuiz?.questions;
	return Array.isArray(inlineQuestions) ? inlineQuestions : [];
});

const assessmentTitle = computed(() => {
	const titleFromFetched = String(fetchedData.value?.mainOutcome?.title ?? '').trim();
	if (titleFromFetched) return titleFromFetched;
	const titleFromProps = String(props.jsonData?.mainOutcome?.title ?? '').trim();
	if (titleFromProps) return titleFromProps;
	return 'Assessment';
});

const assessmentLeadText = computed(() => {
	const leadFromFetched = String(fetchedData.value?.mainOutcome?.leadText ?? '').trim();
	if (leadFromFetched) return leadFromFetched;
	return String(props.jsonData?.mainOutcome?.leadText ?? '').trim();
});

const QUESTION_IMAGE_PLACEHOLDER = '/graphics/post-image-1.jpg';
const QUESTION_IMAGE_PLACEHOLDER_TITLE = 'Fig. 0 - Cumulative Incidence(%) - Years sice Randomization Graph';

const illustrationList = computed<IllustrationItem[]>(() => {
	const fromFetched = fetchedData.value?.illustration;
	if (Array.isArray(fromFetched)) {
		return fromFetched;
	}
	const fromProps = props.jsonData?.illustration;
	return Array.isArray(fromProps) ? fromProps : [];
});

const passingPercent = computed(() => {
	const candidate = Number(
		fetchedData.value?.assessmentQuiz?.passingPercent ??
		props.jsonData?.assessmentQuiz?.passingPercent ??
		70
	);
	if (!Number.isFinite(candidate)) return 70;
	return Math.max(0, Math.min(100, candidate));
});

const formatDuration = (totalSeconds: number): string => {
	const safeSeconds = Math.max(0, Math.floor(totalSeconds));
	const minutes = Math.floor(safeSeconds / 60)
		.toString()
		.padStart(2, '0');
	const seconds = Math.floor(safeSeconds % 60)
		.toString()
		.padStart(2, '0');
	return `${minutes}:${seconds}`;
};

const assessmentQuizConfig = computed(() => fetchedData.value?.assessmentQuiz ?? props.jsonData?.assessmentQuiz ?? {});

const isNoneValue = (value: unknown): boolean => {
	const normalized = String(value ?? '').trim().toLowerCase();
	return normalized === '' || normalized === 'none' || normalized === 'null' || normalized === 'no-limit' || normalized === 'nolimit';
};

const parsePositiveNumber = (value: unknown): number | null => {
	const candidate = Number(value);
	if (!Number.isFinite(candidate) || candidate <= 0) {
		return null;
	}
	return candidate;
};

const parseDurationFromText = (value: unknown): number | null => {
	const raw = String(value ?? '').trim().toLowerCase();
	if (!raw || isNoneValue(raw)) {
		return null;
	}

	if (/^\d+:\d{1,2}(:\d{1,2})?$/.test(raw)) {
		const chunks = raw.split(':').map((item) => Number(item));
		if (chunks.some((item) => !Number.isFinite(item) || item < 0)) {
			return null;
		}
		if (chunks.length === 2) {
			return chunks[0] * 60 + chunks[1];
		}
		return chunks[0] * 3600 + chunks[1] * 60 + chunks[2];
	}

	const withUnit = raw.match(/^(\d+(?:\.\d+)?)\s*(s|sec|secs|second|seconds|m|min|mins|minute|minutes|h|hr|hrs|hour|hours)$/);
	if (withUnit) {
		const amount = Number(withUnit[1]);
		if (!Number.isFinite(amount) || amount <= 0) {
			return null;
		}
		const unit = withUnit[2];
		if (unit.startsWith('h')) return Math.round(amount * 3600);
		if (unit.startsWith('m')) return Math.round(amount * 60);
		return Math.round(amount);
	}

	return null;
};

const parseDurationSeconds = (config: AssessmentSource['assessmentQuiz']): number | null => {
	if (!config) {
		return null;
	}

	const explicitSeconds = [
		config.timeLimitSeconds,
		config.durationSeconds
	];
	for (const item of explicitSeconds) {
		const parsed = parsePositiveNumber(item);
		if (parsed !== null) {
			return Math.round(parsed);
		}
	}

	const explicitMinutes = [
		config.timeLimitMinutes,
		config.durationMinutes
	];
	for (const item of explicitMinutes) {
		const parsed = parsePositiveNumber(item);
		if (parsed !== null) {
			return Math.round(parsed * 60);
		}
	}

	const genericValue = config.timeLimit ?? config.duration;
	if (isNoneValue(genericValue)) {
		return null;
	}

	const parsedText = parseDurationFromText(genericValue);
	if (parsedText !== null) {
		return parsedText;
	}

	const genericNumber = parsePositiveNumber(genericValue);
	if (genericNumber === null) {
		return null;
	}

	const unit = String(config.timeLimitUnit ?? config.durationUnit ?? '').trim().toLowerCase();
	if (unit.startsWith('sec')) {
		return Math.round(genericNumber);
	}
	return Math.round(genericNumber * 60);
};

const assessmentTimeLimitSeconds = computed(() => {
	const fromQuizConfig = parseDurationSeconds(assessmentQuizConfig.value);
	if (fromQuizConfig !== null) {
		return fromQuizConfig;
	}

	const fallbackSources = [fetchedData.value as any, props.jsonData as any];
	for (const source of fallbackSources) {
		if (!source || typeof source !== 'object') {
			continue;
		}
		const fallbackConfig: AssessmentSource['assessmentQuiz'] = {
			timeLimit: source.assessmentTimeLimit ?? source.timeLimit ?? source.duration,
			timeLimitUnit: source.assessmentTimeLimitUnit ?? source.timeLimitUnit ?? source.durationUnit,
			timeLimitSeconds: source.assessmentTimeLimitSeconds ?? source.timeLimitSeconds,
			timeLimitMinutes: source.assessmentTimeLimitMinutes ?? source.timeLimitMinutes,
			durationSeconds: source.durationSeconds,
			durationMinutes: source.durationMinutes
		};
		const parsed = parseDurationSeconds(fallbackConfig);
		if (parsed !== null) {
			return parsed;
		}
	}

	return null;
});
const hasTimeLimit = computed(() => assessmentTimeLimitSeconds.value !== null);
const formattedTimeLimit = computed(() => {
	if (!hasTimeLimit.value) {
		return 'No time limit';
	}
	return formatDuration(assessmentTimeLimitSeconds.value ?? 0);
});
const userConsentText = computed(() => {
	const fromConfig = [
		assessmentQuizConfig.value?.userConsentText,
		assessmentQuizConfig.value?.consentText,
		(fetchedData.value as any)?.userConsentText,
		(props.jsonData as any)?.userConsentText
	]
		.map((value) => String(value ?? '').trim())
		.find((value) => value.length > 0);
	if (fromConfig) {
		return fromConfig;
	}
	return 'Before starting, please confirm that you consent to assessment session monitoring for quality assurance, compliance, and academic integrity.';
});

const DEFAULT_COMPETENCY_WEIGHTS: Record<string, number> = {
	knowledge: 1,
	application: 1.2,
	analysis: 1.3,
	synthesis: 1.4,
	evaluation: 1.5,
	default: 1
};

const DEFAULT_QUESTION_TYPE_WEIGHTS: Record<string, number> = {
	mcq: 1,
	multiplechoice: 1,
	singlechoice: 1,
	multiselect: 1.15,
	casebased: 1.2,
	scenario: 1.15,
	shortanswer: 1.1,
	truefalse: 0.8,
	default: 1
};

const DEFAULT_CRITICAL_LEVEL_WEIGHTS: Record<string, number> = {
	low: 1,
	medium: 1.25,
	high: 1.5,
	critical: 2,
	default: 1
};

const normalizeWeightKey = (value: unknown): string =>
	String(value ?? '')
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '');

const roundPoints = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

const toPositiveWeight = (value: unknown): number | null => {
	const candidate = Number(value);
	if (!Number.isFinite(candidate) || candidate <= 0) {
		return null;
	}
	return candidate;
};

const readWeightMap = (source: unknown): Record<string, number> => {
	if (!source || typeof source !== 'object') {
		return {};
	}

	const output: Record<string, number> = {};
	Object.entries(source as Record<string, unknown>).forEach(([key, value]) => {
		const normalizedKey = normalizeWeightKey(key);
		const parsed = toPositiveWeight(value);
		if (normalizedKey && parsed !== null) {
			output[normalizedKey] = parsed;
		}
	});
	return output;
};

const scoringWeightSource = computed(() => {
	return (
		assessmentQuizConfig.value?.scoringWeights ??
		assessmentQuizConfig.value?.criteriaWeights ??
		({})
	) as Record<string, unknown>;
});

const competencyWeightMap = computed(() =>
	readWeightMap((scoringWeightSource.value as any)?.competency ?? {})
);
const questionTypeWeightMap = computed(() =>
	readWeightMap(
		(scoringWeightSource.value as any)?.typeOfQuestion ??
		(scoringWeightSource.value as any)?.questionType ??
		{}
	)
);
const criticalLevelWeightMap = computed(() =>
	readWeightMap((scoringWeightSource.value as any)?.criticalLevel ?? {})
);

const resolveWeight = (label: string, configured: Record<string, number>, defaults: Record<string, number>): number => {
	const normalized = normalizeWeightKey(label);
	const fallbackDefault = defaults.default ?? 1;
	if (normalized && configured[normalized] !== undefined) {
		return configured[normalized];
	}
	if (normalized && defaults[normalized] !== undefined) {
		return defaults[normalized];
	}
	if (configured.default !== undefined) {
		return configured.default;
	}
	return fallbackDefault;
};

const getFirstTextValue = (source: unknown, keys: string[]): string => {
	if (!source || typeof source !== 'object') {
		return '';
	}
	const asRecord = source as Record<string, unknown>;
	for (const key of keys) {
		const raw = asRecord[key];
		const value = String(raw ?? '').trim();
		if (value) {
			return value;
		}
	}
	return '';
};

const getQuestionCriteria = (question: QuizQuestionsItem | undefined) => {
	const q = (question ?? {}) as Record<string, unknown>;
	const competency =
		getFirstTextValue(q, ['competency', 'compentency', 'competence', 'competencyName', 'domain', 'skill']) || 'General';
	const questionType =
		getFirstTextValue(q, ['typeOfQuestion', 'questionType', 'question_type', 'type', 'typeQuestion']) || 'Standard';
	const criticalLevel =
		getFirstTextValue(q, ['criticalLevel', 'criticality', 'critical_level', 'riskLevel', 'priority']) || 'Normal';

	return {
		competency,
		questionType,
		criticalLevel
	};
};

const selectedAnswers = ref<number[][]>([]);
const currentQuestionIndex = ref(0);
const validationMessage = ref('');
const isCompleted = ref(false);
const hasStarted = ref(false);
const hasMonitoringConsent = ref(false);
const startValidationMessage = ref('');
const elapsedSeconds = ref(0);
const remainingSeconds = ref<number | null>(null);
const attemptHistory = ref<AttemptHistoryItem[]>([]);
let timerHandle: ReturnType<typeof setInterval> | null = null;

const totalQuestions = computed(() => questions.value.length);
const currentQuestionNumber = computed(() => Math.min(currentQuestionIndex.value + 1, Math.max(totalQuestions.value, 1)));
const currentQuestion = computed(() => questions.value[currentQuestionIndex.value]);

const normalizeId = (value: unknown): string => String(value ?? '').trim().toLowerCase();

const getQuestionSelectedImageId = (question: QuizQuestionsItem | undefined): string => {
	const q = (question ?? {}) as Record<string, unknown>;
	const candidates = [
		q.selectedIllustrationId,
		q.selectedImageId,
		q.illustrationId,
		q.imageId,
		q.illustrationIdentifier,
		q.selectedIllustrationIdentifier
	];

	for (const candidate of candidates) {
		const normalized = normalizeId(candidate);
		if (normalized) {
			return normalized;
		}
	}
	return '';
};

const getQuestionDirectImageSrc = (question: QuizQuestionsItem | undefined): string => {
	const q = (question ?? {}) as Record<string, unknown>;
	const directCandidates = [
		q.imageSrc,
		q.selectedImageSrc,
		q.questionImage,
		q.questionImageSrc,
		(q.selectedIllustration as Record<string, unknown> | undefined)?.imageSrc,
		(q.selectedIllustration as Record<string, unknown> | undefined)?.imageSrcEdit,
		(q.illustration as Record<string, unknown> | undefined)?.imageSrc,
		(q.illustration as Record<string, unknown> | undefined)?.imageSrcEdit
	];

	for (const candidate of directCandidates) {
		const value = String(candidate ?? '').trim();
		if (value) {
			return value;
		}
	}
	return '';
};

const getQuestionDirectImageTitle = (question: QuizQuestionsItem | undefined): string => {
	const q = (question ?? {}) as Record<string, unknown>;
	const directCandidates = [
		q.imageTitle,
		q.selectedImageTitle,
		q.questionImageTitle,
		(q.selectedIllustration as Record<string, unknown> | undefined)?.imageTitle,
		(q.illustration as Record<string, unknown> | undefined)?.imageTitle
	];

	for (const candidate of directCandidates) {
		const value = String(candidate ?? '').trim();
		if (value) {
			return value;
		}
	}
	return '';
};

const resolveQuestionImageFromIllustrations = (question: QuizQuestionsItem | undefined): string => {
	const selectedId = getQuestionSelectedImageId(question);
	if (!selectedId) {
		return '';
	}

	const match = illustrationList.value.find((item) => {
		const itemId = normalizeId(item.id);
		const itemIdentifier = normalizeId(item.identifier);
		return selectedId === itemId || selectedId === itemIdentifier;
	});

	if (!match) {
		return '';
	}

	return String(match.imageSrc ?? match.imageSrcEdit ?? '').trim();
};

const resolveQuestionImageTitleFromIllustrations = (question: QuizQuestionsItem | undefined): string => {
	const selectedId = getQuestionSelectedImageId(question);
	if (!selectedId) {
		return '';
	}

	const match = illustrationList.value.find((item) => {
		const itemId = normalizeId(item.id);
		const itemIdentifier = normalizeId(item.identifier);
		return selectedId === itemId || selectedId === itemIdentifier;
	});

	if (!match) {
		return '';
	}

	return String(match.imageTitle ?? '').trim();
};

const currentQuestionImageSrc = computed(() => {
	const direct = getQuestionDirectImageSrc(currentQuestion.value);
	if (direct) {
		return direct;
	}

	const fromIllustrationList = resolveQuestionImageFromIllustrations(currentQuestion.value);
	if (fromIllustrationList) {
		return fromIllustrationList;
	}

	return QUESTION_IMAGE_PLACEHOLDER;
});

const currentQuestionImageTitle = computed(() => {
	const direct = getQuestionDirectImageTitle(currentQuestion.value);
	if (direct) {
		return direct;
	}

	const fromIllustrationList = resolveQuestionImageTitleFromIllustrations(currentQuestion.value);
	if (fromIllustrationList) {
		return fromIllustrationList;
	}

	return QUESTION_IMAGE_PLACEHOLDER_TITLE;
});

const questionImageError = ref(false);
const displayedQuestionImageSrc = computed(() =>
	questionImageError.value ? QUESTION_IMAGE_PLACEHOLDER : currentQuestionImageSrc.value
);
const displayedQuestionImageTitle = computed(() =>
	questionImageError.value ? QUESTION_IMAGE_PLACEHOLDER_TITLE : currentQuestionImageTitle.value
);

const handleQuestionImageError = () => {
	questionImageError.value = true;
};

watch(currentQuestionIndex, () => {
	questionImageError.value = false;
});

const currentAnswers = computed<QuizAnswer[]>(() => {
	const answers = currentQuestion.value?.answers;
	return Array.isArray(answers) ? (answers as QuizAnswer[]) : [];
});

const getQuestionPoints = (question: QuizQuestionsItem | undefined): number => {
	const raw = Number((question as any)?.points);
	if (Number.isFinite(raw) && raw > 0) {
		return raw;
	}
	return 1;
};

type QuestionScoreDetails = {
	basePoints: number;
	weightedPoints: number;
	competency: string;
	questionType: string;
	criticalLevel: string;
	competencyWeight: number;
	questionTypeWeight: number;
	criticalLevelWeight: number;
	totalWeightMultiplier: number;
};

const getQuestionScoreDetails = (question: QuizQuestionsItem | undefined): QuestionScoreDetails => {
	const basePoints = getQuestionPoints(question);
	const { competency, questionType, criticalLevel } = getQuestionCriteria(question);
	const q = (question ?? {}) as Record<string, unknown>;
	const explicitCompetencyWeight = toPositiveWeight(
		getFirstTextValue(q, ['competencyWeight', 'compentencyWeight', 'weightCompetency'])
	);
	const explicitQuestionTypeWeight = toPositiveWeight(
		getFirstTextValue(q, ['questionTypeWeight', 'typeOfQuestionWeight', 'weightQuestionType'])
	);
	const explicitCriticalLevelWeight = toPositiveWeight(
		getFirstTextValue(q, ['criticalLevelWeight', 'weightCriticalLevel', 'criticalWeight'])
	);

	const competencyWeight =
		explicitCompetencyWeight ??
		resolveWeight(competency, competencyWeightMap.value, DEFAULT_COMPETENCY_WEIGHTS);
	const questionTypeWeight =
		explicitQuestionTypeWeight ??
		resolveWeight(questionType, questionTypeWeightMap.value, DEFAULT_QUESTION_TYPE_WEIGHTS);
	const criticalLevelWeight =
		explicitCriticalLevelWeight ??
		resolveWeight(criticalLevel, criticalLevelWeightMap.value, DEFAULT_CRITICAL_LEVEL_WEIGHTS);
	const totalWeightMultiplier = competencyWeight * questionTypeWeight * criticalLevelWeight;
	const weightedPoints = roundPoints(basePoints * totalWeightMultiplier);

	return {
		basePoints,
		weightedPoints,
		competency,
		questionType,
		criticalLevel,
		competencyWeight: roundPoints(competencyWeight),
		questionTypeWeight: roundPoints(questionTypeWeight),
		criticalLevelWeight: roundPoints(criticalLevelWeight),
		totalWeightMultiplier: roundPoints(totalWeightMultiplier)
	};
};

const currentQuestionScoreDetails = computed(() => getQuestionScoreDetails(currentQuestion.value));
const currentQuestionPoints = computed(() => currentQuestionScoreDetails.value.weightedPoints);

const getCorrectIndexes = (question: QuizQuestionsItem | undefined): number[] => {
	const answers = Array.isArray(question?.answers) ? question.answers : [];
	const indexes: number[] = [];
	answers.forEach((answer, index) => {
		if (answer?.isCorrect === true) {
			indexes.push(index);
		}
	});
	return indexes;
};

const currentQuestionIsMulti = computed(() => getCorrectIndexes(currentQuestion.value).length > 1);
const isLastQuestion = computed(() => currentQuestionIndex.value >= totalQuestions.value - 1);

const answeredCount = computed(() =>
	selectedAnswers.value.reduce((acc, entry) => acc + (entry.length > 0 ? 1 : 0), 0)
);
const unansweredCount = computed(() => Math.max(0, totalQuestions.value - answeredCount.value));
const progressWidth = computed(() => {
	if (totalQuestions.value === 0) return '0%';
	return `${Math.round((answeredCount.value / totalQuestions.value) * 100)}%`;
});

const optionLabel = (index: number): string => `${String.fromCharCode(65 + index)}:`;

const normalizeSelection = (value: number[]): number[] => {
	return Array.from(new Set(value)).sort((a, b) => a - b);
};

const isExactSelection = (selected: number[], correct: number[]): boolean => {
	const normalizedSelected = normalizeSelection(selected);
	const normalizedCorrect = normalizeSelection(correct);
	if (normalizedSelected.length !== normalizedCorrect.length) {
		return false;
	}
	return normalizedSelected.every((value, index) => value === normalizedCorrect[index]);
};

const isSelected = (questionIndex: number, answerIndex: number): boolean => {
	return selectedAnswers.value[questionIndex]?.includes(answerIndex) ?? false;
};

const toggleAnswer = (questionIndex: number, answerIndex: number) => {
	validationMessage.value = '';
	const selected = selectedAnswers.value[questionIndex] ?? [];
	if (currentQuestionIsMulti.value) {
		selectedAnswers.value[questionIndex] = selected.includes(answerIndex)
			? selected.filter((item) => item !== answerIndex)
			: [...selected, answerIndex];
		return;
	}
	selectedAnswers.value[questionIndex] = [answerIndex];
};

const canProceedCurrentQuestion = computed(() => {
	return (selectedAnswers.value[currentQuestionIndex.value]?.length ?? 0) > 0;
});

const goToPreviousQuestion = () => {
	validationMessage.value = '';
	if (currentQuestionIndex.value > 0) {
		currentQuestionIndex.value -= 1;
	}
};

const goToNextQuestion = () => {
	if (!canProceedCurrentQuestion.value) {
		validationMessage.value = 'Please select an answer before continuing.';
		return;
	}
	validationMessage.value = '';
	if (!isLastQuestion.value) {
		currentQuestionIndex.value += 1;
	}
};

const formattedElapsed = computed(() => formatDuration(elapsedSeconds.value));

const startAssessment = () => {
	if (!hasMonitoringConsent.value) {
		startValidationMessage.value = 'Please confirm consent before starting the assessment.';
		return;
	}
	startValidationMessage.value = '';
	validationMessage.value = '';
	hasStarted.value = true;
	isCompleted.value = false;
	elapsedSeconds.value = 0;
	remainingSeconds.value = hasTimeLimit.value ? assessmentTimeLimitSeconds.value : null;
	startTimer();
};

const evaluationResults = computed<EvaluationResult[]>(() => {
	return questions.value.map((question, index) => {
		const selected = selectedAnswers.value[index] ?? [];
		const correct = getCorrectIndexes(question);
		const answers = Array.isArray(question.answers) ? (question.answers as QuizAnswer[]) : [];
		const isCorrect = isExactSelection(selected, correct);
		const scoreDetails = getQuestionScoreDetails(question);
		const pointsPossible = scoreDetails.weightedPoints;
		const pointsEarned = isCorrect ? pointsPossible : 0;
		const selectedAnswerLabels = selected.map((answerIndex) => {
			const answer = answers[answerIndex];
			return answer?.answerLabel || optionLabel(answerIndex);
		});
		const correctAnswerLabels = correct.map((answerIndex) => {
			const answer = answers[answerIndex];
			return answer?.answerLabel || optionLabel(answerIndex);
		});
		const explanationFromCorrect = correct
			.map((answerIndex) => String(answers[answerIndex]?.explanation ?? '').trim())
			.find((value) => value.length > 0) ?? '';
		return {
			questionNumber: index + 1,
			questionText: String(question.question ?? ''),
			isCorrect,
			basePoints: scoreDetails.basePoints,
			pointsEarned,
			pointsPossible,
			competency: scoreDetails.competency,
			questionType: scoreDetails.questionType,
			criticalLevel: scoreDetails.criticalLevel,
			competencyWeight: scoreDetails.competencyWeight,
			questionTypeWeight: scoreDetails.questionTypeWeight,
			criticalLevelWeight: scoreDetails.criticalLevelWeight,
			totalWeightMultiplier: scoreDetails.totalWeightMultiplier,
			selectedAnswerLabels,
			correctAnswerLabels,
			explanation: explanationFromCorrect
		};
	});
});

const correctAnswers = computed(() => evaluationResults.value.filter((item) => item.isCorrect).length);
const totalPoints = computed(() => evaluationResults.value.reduce((acc, item) => acc + item.pointsPossible, 0));
const earnedPoints = computed(() => evaluationResults.value.reduce((acc, item) => acc + item.pointsEarned, 0));
const scorePercentage = computed(() => {
	if (totalPoints.value <= 0) return 0;
	return Math.round((earnedPoints.value / totalPoints.value) * 100);
});

const buildCriteriaScores = (
	items: EvaluationResult[],
	key: 'competency' | 'questionType' | 'criticalLevel'
): CriteriaScoreItem[] => {
	const map = new Map<string, CriteriaScoreItem>();
	items.forEach((item) => {
		const label = String(item[key] ?? '').trim() || 'Unspecified';
		const existing = map.get(label) ?? {
			label,
			earnedPoints: 0,
			totalPoints: 0,
			questionCount: 0
		};
		existing.earnedPoints = roundPoints(existing.earnedPoints + item.pointsEarned);
		existing.totalPoints = roundPoints(existing.totalPoints + item.pointsPossible);
		existing.questionCount += 1;
		map.set(label, existing);
	});

	return Array.from(map.values()).sort((a, b) => b.totalPoints - a.totalPoints);
};

const competencyScores = computed(() => buildCriteriaScores(evaluationResults.value, 'competency'));
const questionTypeScores = computed(() => buildCriteriaScores(evaluationResults.value, 'questionType'));
const criticalLevelScores = computed(() => buildCriteriaScores(evaluationResults.value, 'criticalLevel'));
const retakeCount = computed(() => Math.max(0, attemptHistory.value.length - 1));

type SubmitAssessmentOptions = {
	skipCurrentAnswerValidation?: boolean;
	skipUnansweredConfirm?: boolean;
	timedOut?: boolean;
};

const submitAssessment = (options: SubmitAssessmentOptions = {}) => {
	if (!options.skipCurrentAnswerValidation && !canProceedCurrentQuestion.value) {
		validationMessage.value = 'Please select an answer before submitting.';
		return;
	}

	validationMessage.value = '';
	if (!options.skipUnansweredConfirm && unansweredCount.value > 0) {
		const continueSubmit = window.confirm(
			`You have ${unansweredCount.value} unanswered question(s). Submit assessment anyway?`
		);
		if (!continueSubmit) {
			return;
		}
	}

	stopTimer();
	hasStarted.value = false;
	isCompleted.value = true;

	const attemptNumber = attemptHistory.value.length + 1;
	attemptHistory.value = [
		...attemptHistory.value,
		{
			attempt: attemptNumber,
			percentage: scorePercentage.value,
			earnedPoints: earnedPoints.value,
			totalPoints: totalPoints.value,
			correctAnswers: correctAnswers.value,
			totalQuestions: totalQuestions.value,
			elapsedSeconds: elapsedSeconds.value,
			createdAt: new Date().toISOString()
		}
	];

	emits('submit', {
		mode: 'assessment',
		title: assessmentTitle.value,
		totalQuestions: totalQuestions.value,
		answeredQuestions: answeredCount.value,
		correctAnswers: correctAnswers.value,
		totalPoints: totalPoints.value,
		earnedPoints: earnedPoints.value,
		percentage: scorePercentage.value,
		elapsedSeconds: elapsedSeconds.value,
		timeLimitSeconds: assessmentTimeLimitSeconds.value,
		timedOut: options.timedOut === true,
		attempt: attemptNumber,
		retakeCount: Math.max(0, attemptNumber - 1),
		competencyScores: competencyScores.value,
		questionTypeScores: questionTypeScores.value,
		criticalLevelScores: criticalLevelScores.value,
		attemptHistory: attemptHistory.value,
		results: evaluationResults.value
	});
};

const handleRetake = () => {
	stopTimer();
	selectedAnswers.value = questions.value.map(() => []);
	currentQuestionIndex.value = 0;
	validationMessage.value = '';
	startValidationMessage.value = '';
	hasStarted.value = false;
	hasMonitoringConsent.value = false;
	isCompleted.value = false;
	elapsedSeconds.value = 0;
	remainingSeconds.value = hasTimeLimit.value ? assessmentTimeLimitSeconds.value : null;
};

const handleClose = () => {
	emits('close');
};

const startTimer = () => {
	if (timerHandle) {
		clearInterval(timerHandle);
	}
	timerHandle = setInterval(() => {
		if (!hasStarted.value || isCompleted.value) {
			return;
		}

		elapsedSeconds.value += 1;

		if (hasTimeLimit.value && remainingSeconds.value !== null) {
			remainingSeconds.value = Math.max(0, remainingSeconds.value - 1);
			if (remainingSeconds.value === 0) {
				submitAssessment({
					skipCurrentAnswerValidation: true,
					skipUnansweredConfirm: true,
					timedOut: true
				});
			}
		}
	}, 1000);
};

const stopTimer = () => {
	if (timerHandle) {
		clearInterval(timerHandle);
		timerHandle = null;
	}
};

const fetchAssessmentData = async () => {
	if (!props.overrideURL) {
		return;
	}

	isLoading.value = true;
	fetchError.value = null;

	try {
		const response = await fetch(props.overrideURL, {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json'
			}
		});

		const contentType = response.headers.get('content-type');
		if (!contentType || !contentType.includes('application/json')) {
			return;
		}

		if (!response.ok) {
			return;
		}

		const data = await response.json();
		if (data && typeof data === 'object') {
			fetchedData.value = data as AssessmentSource;
		}
	} catch {
		fetchError.value = null;
	} finally {
		isLoading.value = false;
	}
};

watch(questions, (newQuestions) => {
	stopTimer();
	selectedAnswers.value = newQuestions.map(() => []);
	currentQuestionIndex.value = 0;
	validationMessage.value = '';
	startValidationMessage.value = '';
	hasStarted.value = false;
	hasMonitoringConsent.value = false;
	isCompleted.value = false;
	attemptHistory.value = [];
	elapsedSeconds.value = 0;
	remainingSeconds.value = hasTimeLimit.value ? assessmentTimeLimitSeconds.value : null;
}, { immediate: true });

watch(assessmentTimeLimitSeconds, (nextLimit) => {
	if (!hasStarted.value) {
		remainingSeconds.value = nextLimit;
	}
});

onMounted(async () => {
	await fetchAssessmentData();
});

onBeforeUnmount(() => {
	stopTimer();
});
</script>
